import React, { Fragment, useEffect, useState, useCallback } from 'react';
import Footer from '../Footer/Footer';
import Loader from '../../loader/Loader';
import PageSEO from '../Common/PageSEO';
import {
    FeatureHero,
    FeatureHeroCta,
    FeatureChain,
    FeatureSection,
    FeatureDownloadCta,
    FeatureScreenshot
} from '../Common/FeatureContent';
import { useLocale } from '../../localization/LocaleContext';
import { ROUTE_PATHS } from '../../localization/constants';
import './InvoicingMain.css';

const PILLAR_ICONS = [
    (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
            <polyline points="14 2 14 8 20 8"/>
            <line x1="16" y1="13" x2="8" y2="13"/>
            <line x1="16" y1="17" x2="8" y2="17"/>
        </svg>
    ),
    (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
            <circle cx="12" cy="7" r="4"/>
        </svg>
    ),
    (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="8" y1="6" x2="21" y2="6"/>
            <line x1="8" y1="12" x2="21" y2="12"/>
            <line x1="8" y1="18" x2="21" y2="18"/>
            <line x1="3" y1="6" x2="3.01" y2="6"/>
            <line x1="3" y1="12" x2="3.01" y2="12"/>
            <line x1="3" y1="18" x2="3.01" y2="18"/>
        </svg>
    ),
    (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="19" y1="5" x2="5" y2="19"/>
            <circle cx="6.5" cy="6.5" r="2.5"/>
            <circle cx="17.5" cy="17.5" r="2.5"/>
        </svg>
    ),
    (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
            <line x1="16" y1="2" x2="16" y2="6"/>
            <line x1="8" y1="2" x2="8" y2="6"/>
            <line x1="3" y1="10" x2="21" y2="10"/>
        </svg>
    ),
    (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="4" y1="9" x2="20" y2="9"/>
            <line x1="4" y1="15" x2="20" y2="15"/>
            <line x1="10" y1="3" x2="8" y2="21"/>
            <line x1="16" y1="3" x2="14" y2="21"/>
        </svg>
    ),
    (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
            <polyline points="14 2 14 8 20 8"/>
            <path d="M9.5 15.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5z"/>
        </svg>
    ),
    (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
            <polyline points="22 6 12 13 2 6"/>
        </svg>
    )
];

function InvoicingMain(){
    const { t, locale } = useLocale();
    const p = (key) => t(`featurePages.invoicing.${key}`);

    const [loader, setLoader] = useState(false);
    const [activeStep, setActiveStep] = useState(0);
    const [lightboxImage, setLightboxImage] = useState(null);

    useEffect(() => {
        const timer = setTimeout(() => {
            setLoader(true);
        }, 1000);

        return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
        function handleKeyDown(e) {
            if (e.key === 'Escape' && lightboxImage) {
                setLightboxImage(null);
            }
        }
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [lightboxImage]);

    const openLightbox = useCallback((imageObj) => {
        setLightboxImage(imageObj);
    }, []);

    const closeLightbox = useCallback(() => {
        setLightboxImage(null);
    }, []);

    if (!loader) {
        return <Loader/>;
    }

    const pillars = [1, 2, 3, 4, 5, 6, 7, 8].map((num) => ({
        icon: PILLAR_ICONS[num - 1],
        title: p(`pillar${num}Title`),
        description: p(`pillar${num}Description`)
    }));

    const workflowSteps = [
        {
            number: '01',
            title: p('step1Title'),
            description: p('step1Description'),
            highlights: [p('step1Highlight1'), p('step1Highlight2'), p('step1Highlight3')],
            imageSrc: `${process.env.PUBLIC_URL}/images/features/invoicing/invoicing-step1-create.png`,
            imageAlt: p('screenshotStep1Alt')
        },
        {
            number: '02',
            title: p('step2Title'),
            description: p('step2Description'),
            highlights: [p('step2Highlight1'), p('step2Highlight2'), p('step2Highlight3')],
            imageSrc: `${process.env.PUBLIC_URL}/images/features/invoicing/invoicing-step2-details.png`,
            imageAlt: p('screenshotStep2Alt')
        },
        {
            number: '03',
            title: p('step3Title'),
            description: p('step3Description'),
            highlights: [p('step3Highlight1'), p('step3Highlight2'), p('step3Highlight3')],
            imageSrc: `${process.env.PUBLIC_URL}/images/features/invoicing/invoicing-step3-result.png`,
            imageAlt: p('screenshotStep3Alt')
        }
    ];

    const currentStep = workflowSteps[activeStep];

    const heroVisual = (
        <FeatureScreenshot
            src={`${process.env.PUBLIC_URL}/images/features/invoicing/invoicing-hero-main.png`}
            alt={p('screenshotHeroAlt')}
            size="lg"
            tilt="none"
            onClick={() => openLightbox({
                src: `${process.env.PUBLIC_URL}/images/features/invoicing/invoicing-hero-main.png`,
                alt: p('screenshotHeroAlt')
            })}
        />
    );

    return(
        <Fragment>
            <PageSEO title={p('metaTitle')} description={p('metaDescription')} path={ROUTE_PATHS.invoicing} locale={locale} />

            <FeatureHero
                eyebrow={p('eyebrow')}
                title={p('heroTitle')}
                highlight={p('heroHighlight')}
                subtitle={p('heroSubtitle')}
                note={p('heroNote')}
                cta={
                    <FeatureHeroCta
                        primaryLabel={t('shared.featureStartFree')}
                        primaryTarget="feature-download"
                        secondaryLabel={t('shared.featureSeeHowItWorks')}
                        secondaryTarget="feature-content"
                    />
                }
                image={heroVisual}
            />

            {/* Feature Pillars Grid */}
            <section id="feature-content" className="inv-pillars-section">
                <div className="container">
                    <div className="fc-section-header">
                        <h2 className="h-1 h-bold fc-section-title">{p('pillarsSectionTitle')}</h2>
                        <p className="fc-section-description">{p('pillarsSectionDescription')}</p>
                    </div>
                    <div className="inv-pillars-grid">
                        {pillars.map((pillar, idx) => (
                            <div className="inv-pillar-card" key={idx}>
                                <div className="inv-pillar-icon-box">{pillar.icon}</div>
                                <h3 className="inv-pillar-title">{pillar.title}</h3>
                                <p className="inv-pillar-desc">{pillar.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Interactive Workflow Section */}
            <FeatureSection
                alt
                title={p('workflowSectionTitle')}
                description={p('workflowSectionDescription')}
            >
                <FeatureChain steps={[p('workflowChain1'), p('workflowChain2'), p('workflowChain3')]} />

                <div className="inv-workflow-tabs">
                    {workflowSteps.map((step, idx) => (
                        <button
                            key={idx}
                            className={`inv-workflow-tab${activeStep === idx ? ' active' : ''}`}
                            onClick={() => setActiveStep(idx)}
                            type="button"
                        >
                            <span className="inv-workflow-tab-num">{step.number}</span>
                            <span>{step.title}</span>
                        </button>
                    ))}
                </div>

                <div className="inv-showcase-box">
                    <div className="inv-showcase-row">
                        <div className="inv-showcase-content">
                            <span className="inv-showcase-badge">{p('stepProgress').replace('{{number}}', currentStep.number)}</span>
                            <h3 className="inv-showcase-title">{currentStep.title}</h3>
                            <p className="inv-showcase-description">{currentStep.description}</p>

                            <ul className="inv-checklist">
                                {currentStep.highlights.map((h, i) => (
                                    <li key={i} className="inv-checklist-item">
                                        <svg className="inv-checklist-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                            <polyline points="20 6 9 17 4 12"></polyline>
                                        </svg>
                                        <span>{h}</span>
                                    </li>
                                ))}
                            </ul>

                            <div className="inv-showcase-nav">
                                <button
                                    type="button"
                                    className="inv-nav-btn"
                                    disabled={activeStep === 0}
                                    onClick={() => setActiveStep(prev => Math.max(0, prev - 1))}
                                >
                                    ← {p('previous')}
                                </button>
                                <button
                                    type="button"
                                    className="inv-nav-btn inv-nav-btn-primary"
                                    disabled={activeStep === workflowSteps.length - 1}
                                    onClick={() => setActiveStep(prev => Math.min(workflowSteps.length - 1, prev + 1))}
                                >
                                    {p('nextStep')} →
                                </button>
                            </div>
                        </div>

                        <div
                            className="inv-showcase-image-wrapper"
                            onClick={() => openLightbox({
                                src: currentStep.imageSrc,
                                alt: currentStep.imageAlt,
                                caption: `${currentStep.number}. ${currentStep.title}`
                            })}
                        >
                            <FeatureScreenshot
                                src={currentStep.imageSrc}
                                alt={currentStep.imageAlt}
                                size="md"
                                tilt="none"
                            />
                            <span className="inv-zoom-hint">🔍 {p('clickToZoom')}</span>
                        </div>
                    </div>
                </div>
            </FeatureSection>

            {/* Before / After Comparison Section */}
            <section className="inv-comparison-section">
                <div className="container">
                    <div className="inv-comparison-header">
                        <span className="inv-comparison-eyebrow">{p('beforeAfterEyebrow')}</span>
                        <h2 className="h-1 h-bold fc-section-title">{p('beforeAfterTitle')}</h2>
                        <p className="fc-section-description">{p('beforeAfterDescription')}</p>
                    </div>

                    <div className="inv-comparison-row">
                        <div className="inv-comparison-col">
                            <div className="inv-comparison-card-tag">
                                <span className="inv-tag-pill inv-tag-pill-before">{p('beforeLabel')}</span>
                            </div>
                            <div
                                className="inv-showcase-image-wrapper"
                                onClick={() => openLightbox({
                                    src: `${process.env.PUBLIC_URL}/images/features/invoicing/invoicing-manual-mess.png`,
                                    alt: p('screenshotBeforeAlt'),
                                    caption: p('beforeLabel')
                                })}
                            >
                                <FeatureScreenshot
                                    src={`${process.env.PUBLIC_URL}/images/features/invoicing/invoicing-manual-mess.png`}
                                    alt={p('screenshotBeforeAlt')}
                                    size="md"
                                    tilt="left"
                                />
                                <span className="inv-zoom-hint">🔍 {p('clickToZoom')}</span>
                            </div>
                            <p className="inv-comparison-caption">{p('beforeCaption')}</p>
                        </div>

                        <div className="inv-comparison-divider-col">
                            <div className="inv-arrow-badge" title={p('switchToInvoicePoint')}>
                                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="9 18 15 12 9 6"></polyline>
                                </svg>
                            </div>
                        </div>

                        <div className="inv-comparison-col">
                            <div className="inv-comparison-card-tag">
                                <span className="inv-tag-pill inv-tag-pill-after">{p('afterLabel')}</span>
                            </div>
                            <div
                                className="inv-showcase-image-wrapper"
                                onClick={() => openLightbox({
                                    src: `${process.env.PUBLIC_URL}/images/features/invoicing/invoicing-pro-clean.png`,
                                    alt: p('screenshotAfterAlt'),
                                    caption: p('afterLabel')
                                })}
                            >
                                <FeatureScreenshot
                                    src={`${process.env.PUBLIC_URL}/images/features/invoicing/invoicing-pro-clean.png`}
                                    alt={p('screenshotAfterAlt')}
                                    size="md"
                                    tilt="right"
                                />
                                <span className="inv-zoom-hint">🔍 {p('clickToZoom')}</span>
                            </div>
                            <p className="inv-comparison-caption">{p('afterCaption')}</p>
                        </div>
                    </div>
                </div>
            </section>

            <FeatureDownloadCta
                title={p('ctaTitle')}
                description={p('ctaDescription')}
                ctaLabel={p('ctaButton')}
                storesLabel={t('shared.featureDownloadApp')}
            />

            {/* Lightbox Zoom Modal */}
            {lightboxImage && (
                <div className="inv-lightbox-overlay" onClick={closeLightbox}>
                    <div className="inv-lightbox-content" onClick={(e) => e.stopPropagation()}>
                        <button
                            type="button"
                            className="inv-lightbox-close"
                            onClick={closeLightbox}
                            aria-label={p('closeImagePreview')}
                        >
                            ✕
                        </button>
                        <div className="inv-lightbox-img-wrapper">
                            <img
                                src={lightboxImage.src}
                                alt={lightboxImage.alt}
                                className="inv-lightbox-img"
                            />
                        </div>
                        {lightboxImage.caption && (
                            <p className="inv-lightbox-caption">{lightboxImage.caption}</p>
                        )}
                    </div>
                </div>
            )}

            <Footer />
        </Fragment>
    )
}

export default InvoicingMain;